export interface SportConfig {
  periods: number;
  periodNames: string[];
  periodType: string; // Quarter, Half, Round, Set, Inning, Frame, Hole, etc.
  periodLength: number; // in seconds, 0 means no time limit
  countDown: boolean;
  scoringOptions: number[]; // Available point values for scoring
  scoringType: 'goals' | 'points' | 'runs' | 'sets' | 'tries';
  maxScore?: number; // For some sports like volleyball
  winByMargin?: number; // For sports requiring margin of victory
}

export interface SportVariant {
  id: string;
  sport_id: number;
  variant_name: string;
  variant_description: string;
  min_players_per_team: number;
  max_players_per_team: number;
  total_teams: number;
  is_default_variant: boolean;
  display_format: string; // e.g., "5v5", "11v11", "Singles"
  field_size: string; // e.g., "Full Court", "Half Court"
}

export const SPORT_CONFIGS: Record<string, SportConfig> = {
  // Ball Sports
  'Basketball': {
    periods: 4,
    periodNames: ['1st Quarter', '2nd Quarter', '3rd Quarter', '4th Quarter'],
    periodType: 'Quarter',
    periodLength: 12 * 60, // 12 minutes
    countDown: true,
    scoringOptions: [1, 2, 3],
    scoringType: 'points'
  },
  'Football': {
    periods: 4,
    periodNames: ['1st Quarter', '2nd Quarter', '3rd Quarter', '4th Quarter'],
    periodType: 'Quarter',
    periodLength: 15 * 60, // 15 minutes
    countDown: true,
    scoringOptions: [1, 2, 3, 6, 7, 8],
    scoringType: 'points'
  },
  'Soccer': {
    periods: 2,
    periodNames: ['1st Half', '2nd Half'],
    periodType: 'Half',
    periodLength: 45 * 60, // 45 minutes
    countDown: false,
    scoringOptions: [1],
    scoringType: 'goals'
  },
  'Baseball': {
    periods: 9,
    periodNames: Array.from({length: 9}, (_, i) => `Inning ${i + 1}`),
    periodType: 'Inning',
    periodLength: 0, // No time limit
    countDown: false,
    scoringOptions: [1, 2, 3, 4],
    scoringType: 'runs'
  },
  'Volleyball': {
    periods: 5,
    periodNames: ['Set 1', 'Set 2', 'Set 3', 'Set 4', 'Set 5'],
    periodType: 'Set',
    periodLength: 0, // No time limit
    countDown: false,
    scoringOptions: [1],
    scoringType: 'points',
    maxScore: 25,
    winByMargin: 2
  },
  'Tennis': {
    periods: 5,
    periodNames: ['Set 1', 'Set 2', 'Set 3', 'Set 4', 'Set 5'],
    periodType: 'Set',
    periodLength: 0, // No time limit
    countDown: false,
    scoringOptions: [1],
    scoringType: 'points'
  },
  'Hockey': {
    periods: 3,
    periodNames: ['1st Period', '2nd Period', '3rd Period'],
    periodType: 'Period',
    periodLength: 20 * 60, // 20 minutes
    countDown: true,
    scoringOptions: [1],
    scoringType: 'goals'
  },
  'Rugby': {
    periods: 2,
    periodNames: ['1st Half', '2nd Half'],
    periodType: 'Half',
    periodLength: 40 * 60, // 40 minutes
    countDown: false,
    scoringOptions: [1, 2, 3, 5, 7],
    scoringType: 'points'
  },
  'Cricket': {
    periods: 2,
    periodNames: ['1st Innings', '2nd Innings'],
    periodType: 'Innings',
    periodLength: 0, // No time limit
    countDown: false,
    scoringOptions: [1, 2, 3, 4, 6],
    scoringType: 'runs'
  },
  'Badminton': {
    periods: 3,
    periodNames: ['Game 1', 'Game 2', 'Game 3'],
    periodType: 'Game',
    periodLength: 0, // No time limit
    countDown: false,
    scoringOptions: [1],
    scoringType: 'points',
    maxScore: 21,
    winByMargin: 2
  },
  
  // Combat Sports
  'Boxing': {
    periods: 12,
    periodNames: Array.from({length: 12}, (_, i) => `Round ${i + 1}`),
    periodType: 'Round',
    periodLength: 3 * 60, // 3 minutes
    countDown: true,
    scoringOptions: [10, 9, 8, 7],
    scoringType: 'points'
  },
  'Wrestling': {
    periods: 3,
    periodNames: ['Period 1', 'Period 2', 'Period 3'],
    periodType: 'Period',
    periodLength: 2 * 60, // 2 minutes
    countDown: true,
    scoringOptions: [1, 2, 3, 4, 5, 6],
    scoringType: 'points'
  },
  'MMA': {
    periods: 5,
    periodNames: Array.from({length: 5}, (_, i) => `Round ${i + 1}`),
    periodType: 'Round',
    periodLength: 5 * 60, // 5 minutes
    countDown: true,
    scoringOptions: [10, 9, 8, 7],
    scoringType: 'points'
  },
  'Karate': {
    periods: 3,
    periodNames: Array.from({length: 3}, (_, i) => `Round ${i + 1}`),
    periodType: 'Round',
    periodLength: 3 * 60, // 3 minutes
    countDown: true,
    scoringOptions: [1, 2, 3],
    scoringType: 'points'
  },
  'Judo': {
    periods: 1,
    periodNames: ['Match'],
    periodType: 'Match',
    periodLength: 4 * 60, // 4 minutes
    countDown: true,
    scoringOptions: [1, 7, 10], // Waza-ari, Ippon scores
    scoringType: 'points'
  },
  'Taekwondo': {
    periods: 3,
    periodNames: Array.from({length: 3}, (_, i) => `Round ${i + 1}`),
    periodType: 'Round',
    periodLength: 2 * 60, // 2 minutes
    countDown: true,
    scoringOptions: [1, 2, 3, 4, 5],
    scoringType: 'points'
  },
  
  // Track & Field
  'Track and Field': {
    periods: 1,
    periodNames: ['Event'],
    periodType: 'Event',
    periodLength: 0, // Varies by event
    countDown: false,
    scoringOptions: [1, 2, 3, 4, 5, 6, 7, 8],
    scoringType: 'points'
  },
  'Marathon': {
    periods: 1,
    periodNames: ['Race'],
    periodType: 'Race',
    periodLength: 0, // No time limit
    countDown: false,
    scoringOptions: [1],
    scoringType: 'points'
  },
  'Swimming': {
    periods: 1,
    periodNames: ['Race'],
    periodType: 'Race',
    periodLength: 0, // Varies by distance
    countDown: false,
    scoringOptions: [1, 2, 3, 4, 5, 6, 7, 8],
    scoringType: 'points'
  },
  'Cycling': {
    periods: 1,
    periodNames: ['Race'],
    periodType: 'Race',
    periodLength: 0, // Varies by event
    countDown: false,
    scoringOptions: [1, 2, 3, 4, 5],
    scoringType: 'points'
  },
  
  // Racquet Sports
  'Table Tennis': {
    periods: 7,
    periodNames: Array.from({length: 7}, (_, i) => `Game ${i + 1}`),
    periodType: 'Game',
    periodLength: 0, // No time limit
    countDown: false,
    scoringOptions: [1],
    scoringType: 'points',
    maxScore: 11,
    winByMargin: 2
  },
  'Squash': {
    periods: 5,
    periodNames: Array.from({length: 5}, (_, i) => `Game ${i + 1}`),
    periodType: 'Game',
    periodLength: 0, // No time limit
    countDown: false,
    scoringOptions: [1],
    scoringType: 'points',
    maxScore: 11,
    winByMargin: 2
  },
  
  // Team Sports
  'Handball': {
    periods: 2,
    periodNames: ['1st Half', '2nd Half'],
    periodType: 'Half',
    periodLength: 30 * 60, // 30 minutes
    countDown: true,
    scoringOptions: [1],
    scoringType: 'goals'
  },
  'Water Polo': {
    periods: 4,
    periodNames: ['1st Quarter', '2nd Quarter', '3rd Quarter', '4th Quarter'],
    periodType: 'Quarter',
    periodLength: 8 * 60, // 8 minutes
    countDown: true,
    scoringOptions: [1],
    scoringType: 'goals'
  },
  'Lacrosse': {
    periods: 4,
    periodNames: ['1st Quarter', '2nd Quarter', '3rd Quarter', '4th Quarter'],
    periodType: 'Quarter',
    periodLength: 15 * 60, // 15 minutes
    countDown: true,
    scoringOptions: [1],
    scoringType: 'goals'
  },
  'Field Hockey': {
    periods: 4,
    periodNames: ['1st Quarter', '2nd Quarter', '3rd Quarter', '4th Quarter'],
    periodType: 'Quarter',
    periodLength: 15 * 60, // 15 minutes
    countDown: false,
    scoringOptions: [1],
    scoringType: 'goals'
  },
  
  // Individual Sports
  'Golf': {
    periods: 18,
    periodNames: Array.from({length: 18}, (_, i) => `Hole ${i + 1}`),
    periodType: 'Hole',
    periodLength: 0, // No time limit
    countDown: false,
    scoringOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    scoringType: 'points'
  },
  'Bowling': {
    periods: 10,
    periodNames: Array.from({length: 10}, (_, i) => `Frame ${i + 1}`),
    periodType: 'Frame',
    periodLength: 0, // No time limit
    countDown: false,
    scoringOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    scoringType: 'points'
  },
  'Darts': {
    periods: 1,
    periodNames: ['Match'],
    periodType: 'Match',
    periodLength: 0, // No time limit
    countDown: false,
    scoringOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 25, 50],
    scoringType: 'points'
  },
  'Archery': {
    periods: 1,
    periodNames: ['Competition'],
    periodType: 'Competition',
    periodLength: 0, // No time limit
    countDown: false,
    scoringOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    scoringType: 'points'
  },
  
  // Winter Sports
  'Figure Skating': {
    periods: 1,
    periodNames: ['Performance'],
    periodType: 'Performance',
    periodLength: 0, // Varies by program
    countDown: false,
    scoringOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    scoringType: 'points'
  },
  'Skiing': {
    periods: 1,
    periodNames: ['Run'],
    periodType: 'Run',
    periodLength: 0, // Time-based
    countDown: false,
    scoringOptions: [1],
    scoringType: 'points'
  },
  'Snowboarding': {
    periods: 1,
    periodNames: ['Run'],
    periodType: 'Run',
    periodLength: 0, // Time-based
    countDown: false,
    scoringOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    scoringType: 'points'
  }
};

export const getSportConfig = (sportName: string): SportConfig => {
  return SPORT_CONFIGS[sportName] || SPORT_CONFIGS['Basketball'];
};

export const getAvailableSports = (): string[] => {
  return Object.keys(SPORT_CONFIGS).sort();
};

export const getSportVariantDisplayText = (variant: SportVariant): string => {
  if (variant.display_format === 'Individual') {
    return `${variant.variant_name}`;
  }
  return `${variant.display_format} ${variant.variant_name}`;
};

export const getMinPlayersText = (variant: SportVariant): string => {
  if (variant.total_teams === 1) {
    return `${variant.min_players_per_team} participant${variant.min_players_per_team > 1 ? 's' : ''}`;
  }
  return `${variant.min_players_per_team} players per team (${variant.min_players_per_team * variant.total_teams} total)`;
};